import type { PitchFrame } from '@tunasong/music-lib'
import { findAttackFoot, onsetVoicingClarityMin } from './onset-refine.js'
import { detectPitch, pitchHopSize, pitchWindowSize } from './processors/pitch.js'
import { rms } from './utilities.js'

export interface PitchFrameSourceOptions {
  sampleRate: number
  windowSize?: number
  hopSize?: number
}

/**
 * Turns the AudioWorklet's 128-sample blocks into overlapping analysis frames.
 *
 * Deliberately separate from the worklet shell: the buffer arithmetic and the frame timestamps
 * are the part most likely to be wrong, and they are the part a real browser cannot tell you
 * anything useful about. The worklet itself is then thin enough to read.
 *
 * The window is a ring buffer, unwrapped into a scratch array once per hop rather than shifted
 * once per sample — on the audio thread the difference is between two copies per sample and
 * two thousand.
 */
export const createPitchFrameSource = (options: PitchFrameSourceOptions) => {
  const { sampleRate, windowSize = pitchWindowSize, hopSize = pitchHopSize } = options

  const ring = new Float32Array(windowSize)
  const scratch = new Float32Array(windowSize)
  /** Where the next sample is written; also the oldest sample in the ring once it has wrapped. */
  let writeIndex = 0
  /** Samples written since the last emitted frame. */
  let filled = 0
  /** Total samples consumed, so frame times survive the ring wrapping under them. */
  let consumed = 0
  let primed = false
  /** Whether the previous frame was voiced, so an onset can be recognised as a rising edge. */
  let wasVoiced = false

  const emit = (): PitchFrame => {
    for (let i = 0; i < windowSize; i++) {
      scratch[i] = ring[(writeIndex + i) % windowSize]
    }
    const detected = detectPitch(scratch, sampleRate)
    const frameRms = rms(scratch)
    const clarity = detected?.clarity ?? 0
    const voiced = clarity >= onsetVoicingClarityMin

    /*
     * The onset scan runs only on the RISING EDGE of voicing, and only over the window already in
     * hand - which is the current hop and the one before it, exactly the samples in `scratch`. A
     * note that first reads as periodic here began inside that span: the previous frame's window
     * overlapped it by a whole hop and did not read as periodic.
     */
    const foot = voiced && !wasVoiced ? findAttackFoot(scratch, frameRms) : null
    wasVoiced = voiced

    return {
      /** The centre of the window is the least biased estimate of when this pitch was sung. */
      time: (consumed - windowSize / 2) / sampleRate,
      frequency: detected?.frequency ?? 0,
      clarity,
      rms: frameRms,
      /*
       * Relative to `time`, which is the window CENTRE and therefore the first sample of the current
       * hop. `scratch[i]` is absolute sample `consumed - windowSize + i`, so the offset from that
       * centre is `i - windowSize / 2`: negative in the previous hop, non-negative in this one.
       *
       * Spread rather than `onsetSampleOffset: undefined`, so a frame that is not an onset is
       * byte-identical to what it was before this existed - fixture tests compare whole frames, and
       * a present-but-undefined key is not the same object as a missing one.
       */
      ...(foot === null ? {} : { onsetSampleOffset: foot - windowSize / 2 }),
    }
  }

  return {
    push: (block: Float32Array): PitchFrame[] => {
      const frames: PitchFrame[] = []
      for (const sample of block) {
        ring[writeIndex] = sample
        writeIndex = (writeIndex + 1) % windowSize
        consumed++
        filled++

        if (!primed) {
          if (consumed < windowSize) {
            continue
          }
          primed = true
          filled = 0
          frames.push(emit())
          continue
        }
        if (filled >= hopSize) {
          filled = 0
          frames.push(emit())
        }
      }
      return frames
    },
  }
}
